import axios, { type AxiosError } from 'axios';

import i18next from '@/i18n';

import { type CloseNotification, SYSTEM_ACTIONS, bridge } from './vendor/web';

export const nativeLog = (message: string, data?: unknown): void => {
  bridge.send(SYSTEM_ACTIONS.Log, { message, data });
};

/**
 * Закрыть мини-апп. Можно передать `notification` — причину закрытия, которую
 * native покажет пользователю (например, при принудительном выходе из сервиса),
 * чтобы было понятно, почему его «выбросило». Без аргумента — тихое закрытие.
 */
export const closeWebView = (reason?: CloseNotification): void => {
  bridge.send(SYSTEM_ACTIONS.NavigationClose, reason ? { reason } : null);
};

export const navigateBackInHost = (): void => {
  bridge.send(SYSTEM_ACTIONS.NavigationBack, null);
};

export interface ISsoAuthorizeOptions {
  preventCloseOnError?: boolean;
}

const SSO_AUTHORIZE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
export const ssoAuthorize = async (
  options?: ISsoAuthorizeOptions
): Promise<{
  status: number;
  data: { accessToken: string };
} | null> => {
  try {
    const bridgeResponse = await bridge.request<{
      authorizationCode: string;
      codeVerifier: string;
      expiresInSeconds: number;
    }>(SYSTEM_ACTIONS.SsoAuthorize, null, SSO_AUTHORIZE_TIMEOUT);
    const response = await axios.post(
      process.env.REACT_APP_CLIENT_API_URL + '/online/auth/superapp',
      { sessionToken: bridgeResponse.authorizationCode }
    );
    return response;
  } catch (err) {
    const error = err as AxiosError;
    if (!options?.preventCloseOnError) {
      closeWebView({
        type: 'error',
        message:
          error.response?.status === 401
            ? i18next.t('client.sso-authorization-error')
            : error.message
      });
    }
    return null;
  }
};

export const redirectToMiniApp = (
  miniAppId: 'leasing',
  params?: Record<string, unknown>
) => {
  bridge.send(SYSTEM_ACTIONS.NavigationOpenMiniApp, { miniAppId, params });
};

export const getCurrentPosition = async (): Promise<{
  status: 'ok' | 'denied' | 'error';
  latitude: number;
  longitude: number;
} | null> => {
  return await bridge.request<{
    status: 'ok' | 'denied' | 'error';
    latitude: number;
    longitude: number;
  }>(SYSTEM_ACTIONS.LocationGetCurrentPosition, null);
};

export const saveCityInNative = (city: { uuid: string; name: string }) => {
  bridge.send(SYSTEM_ACTIONS.SaveCity, city);
};

/**
 * Флаг, который определяет, авторизован ли пользователь в приложении.
 * Используется для определения, был ли пользователь авторизован до перехода в webview.
 */
let isAppAuthenticated = false;
/**
 * Получить флаг, который определяет, авторизован ли пользователь в приложении.
 */
export const getIsAppAuthenticated = () => isAppAuthenticated;
export const setIsAppAuthenticated = (value: boolean) => {
  isAppAuthenticated = value;
};
